import React, { useEffect, useState } from "react";
import { generatePath } from "react-router-dom";
import { useUserProfile } from "../Persistence/UserProfileState";
import { useNativeAvatarNotifier } from "../Hooks/useNativeAvatarNotifier";
import { DEVICE_TYPES, useDeviceType } from "../Hooks/useDeviceType";
import { getNativeAppVersion } from "@infrastructure/Helpers/GetNativeAppVersion";

export enum LOCATION_AUTHORIZATION_STATES {
    DENIED = "denied",
    GRANTED = "granted",
    NOT_DETERMINED = "notDetermined",
    UNDEFINED = "undefined",
}

export enum DEVICE_LOCATION_AUTHORIZATION_STATES {
    DENIED = "deniedDeviceLocation",
    GRANTED = "grantedDeviceLocation",
    UNDEFINED = "undefined",
}

export type NativeAppContextData = {
    isForeground: boolean;
    isNotificationsAuthorized: boolean;
    isLocationAuthorized: LOCATION_AUTHORIZATION_STATES;
    isDeviceLocationAuthorized: DEVICE_LOCATION_AUTHORIZATION_STATES;
    isPluginEnabled: boolean;
};

const NativeAppContext = React.createContext<NativeAppContextData>({
    isForeground: true,
    isNotificationsAuthorized: false,
    isLocationAuthorized: LOCATION_AUTHORIZATION_STATES.UNDEFINED,
    isDeviceLocationAuthorized: DEVICE_LOCATION_AUTHORIZATION_STATES.UNDEFINED,
    isPluginEnabled: false,
});

export const useNativeApp = () => {
    return React.useContext(NativeAppContext);
};

export const initialDeviceLocationAuthorization = (
    deviceType: DEVICE_TYPES,
) => {
    const appVersion = getNativeAppVersion();

    if (!appVersion) {
        return DEVICE_LOCATION_AUTHORIZATION_STATES.GRANTED;
    }

    if (
        (deviceType === DEVICE_TYPES.ANDROID_WITH_PLUGIN ||
            deviceType === DEVICE_TYPES.IOS_WITH_PLUGIN) &&
        ((appVersion.major === 2024 && appVersion.minor >= 2) ||
            appVersion.major > 2024)
    ) {
        return DEVICE_LOCATION_AUTHORIZATION_STATES.UNDEFINED;
    } else {
        return DEVICE_LOCATION_AUTHORIZATION_STATES.GRANTED;
    }
};

export const NativeAppProvider = ({ children }) => {
    const { deviceType } = useDeviceType();
    const [isForeground, setIsForeground] = useState(true);
    const [isPluginEnabled, setIsPluginEnabled] = useState(false);
    const [isNotificationsAuthorized, setIsNotificationsAuthorized] =
        useState(true);
    const [isLocationAuthorized, setIsLocationAuthorized] = useState(
        LOCATION_AUTHORIZATION_STATES.UNDEFINED,
    );
    const [isDeviceLocationAuthorized, setIsDeviceLocationAuthorized] =
        useState(initialDeviceLocationAuthorization(deviceType));
    const profile = useUserProfile();
    const nativeAvatarNotifier = useNativeAvatarNotifier();

    const avatar = profile.avatar || null;
    const gender = profile.gender || null;
    const uuid = profile.uuid || null;

    useEffect(() => {
        try {
            nativeAvatarNotifier?.(avatar, gender, uuid);
        } catch (e) {
            console.error("Native app could not handle avatar update.");
        }
    }, [avatar, uuid, gender, nativeAvatarNotifier]);

    useEffect(() => {
        window.C24App?.setHeaderScrollingDisabled?.(true);
        window.C24App?.isPluginEnabled(
            "de.check24.community",
            (success: boolean) => {
                setIsPluginEnabled(success);
            },
        );
    }, []);

    document.nativeCall = (nativeEventString: string) => {
        const nativeEvent = JSON.parse(nativeEventString);

        let status;
        switch (nativeEvent.detail.nativeAppEventName) {
            case "ping":
                status = "success";
                break;
            case "navigateToPath":
                if (nativeEvent?.detail?.payload?.path) {
                    console.log(
                        "navigate to path ",
                        generatePath(nativeEvent.detail.payload.path),
                    );
                    status = "success";
                } else {
                    status = "detailValidationFailed";
                }
                break;
            default:
                status = "nativeAppEventNameNotFound";
        }

        return JSON.stringify({ status });
    };

    useEffect(() => {
        const visibilityListener = (event) => {
            setIsForeground(
                event.detail.visibility.toLowerCase() === "foreground",
            );
        };
        document.addEventListener("native_app_state", visibilityListener);

        const notificationsAuthorizationListener = (event) => {
            setIsNotificationsAuthorized(event.detail.isAuthorized);
        };
        document.addEventListener(
            "notifications_authorization_status",
            notificationsAuthorizationListener,
        );

        const locationAuthorizationListener = (event) => {
            setIsLocationAuthorized(
                event.detail
                    .isLocationAuthorized as LOCATION_AUTHORIZATION_STATES,
            );
        };
        document.addEventListener(
            "location_authorization_status",
            locationAuthorizationListener,
        );

        const deviceLocationAuthorizationListener = (event) => {
            setIsDeviceLocationAuthorized(
                event.detail
                    .isDeviceLocationAuthorized as DEVICE_LOCATION_AUTHORIZATION_STATES,
            );
        };
        document.addEventListener(
            "device_location_status",
            deviceLocationAuthorizationListener,
        );

        const profileListener = () => {
            console.log("navigate to profile ");
        };
        document.addEventListener("native_show_profile", profileListener);

        const dashboardListener = () => {
            console.log("navigate to DashboardIndexUrl ");
        };
        document.addEventListener("native_show_dashboard", dashboardListener);

        const dataFeedListener = (event) => {
            const params = event.detail.dataFeed;
            init(params ?? undefined);
        };
        document.addEventListener("data_feed_changed", dataFeedListener);

        return () => {
            document.removeEventListener(
                "native_app_state",
                visibilityListener,
            );
            document.removeEventListener(
                "notifications_authorization_status",
                notificationsAuthorizationListener,
            );
            document.removeEventListener(
                "native_show_profile",
                profileListener,
            );
            document.removeEventListener(
                "native_show_dashboard",
                dashboardListener,
            );
            document.removeEventListener(
                "location_authorization_status",
                locationAuthorizationListener,
            );
            document.removeEventListener(
                "device_location_status",
                deviceLocationAuthorizationListener,
            );
            document.removeEventListener("data_feed_changed", dataFeedListener);
        };
    }, []);

    const value: NativeAppContextData = {
        isForeground,
        isNotificationsAuthorized,
        isLocationAuthorized,
        isDeviceLocationAuthorized,
        isPluginEnabled,
    };

    return (
        <NativeAppContext.Provider value={value}>
            {children}
        </NativeAppContext.Provider>
    );
};
